import { PaginationControls } from '@components/common/PaginationControls';
import { formatGuestPreviewDate, getGuestPreviewInitials } from '@lib/utils';
import { AlertCircle, Globe, Mail, Phone, Users } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { AccommodationGuest } from '../../../../types/accommodation';

const STATUS_CHIP: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-yellow-50 text-yellow-700' },
  confirmed: { label: 'Confirmed', className: 'bg-blue-50 text-blue-700' },
  checked_in: {
    label: 'Checked In',
    className: 'bg-pos-primary/10 text-pos-primary',
  },
  checked_out: { label: 'Checked Out', className: 'bg-gray-100 text-gray-600' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-600' },
  no_show: { label: 'No Show', className: 'bg-orange-50 text-orange-600' },
};

interface AccommodationGuestsTableProps {
  guests: AccommodationGuest[];
  isLoading: boolean;
  error: string | null;
  search: string;
  onGuestClick: (guest: AccommodationGuest) => void;
}

export const AccommodationGuestsTable: React.FC<
  AccommodationGuestsTableProps
> = ({ guests, isLoading, error, search, onGuestClick }) => {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    setPage(1);
  }, [guests.length, search]);

  const totalItems = guests.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const activePage = Math.min(page, totalPages);

  const paginatedGuests = useMemo(() => {
    const start = (activePage - 1) * pageSize;
    return guests.slice(start, start + pageSize);
  }, [guests, activePage, pageSize]);

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <p className="text-sm text-gray-400">Loading guests…</p>
        </div>
      ) : error ? (
        <div className="flex h-48 flex-col items-center justify-center gap-2 text-center">
          <AlertCircle size={24} className="text-red-400" />
          <p className="text-sm text-red-500">{error}</p>
        </div>
      ) : guests.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center gap-2 text-center">
          <Users size={28} className="text-gray-300" />
          <p className="text-sm font-medium text-gray-500">
            {search ? 'No guests match your search' : 'No guests yet'}
          </p>
          {!search && (
            <p className="text-xs text-gray-400">
              Guests appear here once a booking is created with a client.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-auto max-h-[480px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                <th className="px-4 py-3">Guest</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3">Bookings</th>
                <th className="px-4 py-3">Last Visit</th>
                <th className="px-4 py-3">Booking Method</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {paginatedGuests.map((guest) => {
                const chip = guest.latest_booking_status
                  ? STATUS_CHIP[guest.latest_booking_status]
                  : null;
                const hasBalance = guest.outstanding_credit_balance > 0;

                return (
                  <tr
                    key={guest.id}
                    className="cursor-pointer transition-colors hover:bg-gray-50"
                    onClick={() => onGuestClick(guest)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-pos-primary/10 text-xs font-semibold text-pos-primary">
                          {getGuestPreviewInitials(
                            guest.first_name,
                            guest.last_name,
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {guest.first_name} {guest.last_name}
                          </p>
                          {guest.id_type && (
                            <p className="text-xs text-gray-400">
                              {guest.id_type}
                              {guest.id_number ? ` · ${guest.id_number}` : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="space-y-0.5">
                        {guest.email && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Mail size={11} />
                            {guest.email}
                          </div>
                        )}
                        {guest.contact && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Phone size={11} />
                            {guest.contact}
                          </div>
                        )}
                        {!guest.email && !guest.contact && (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      {guest.country_name ? (
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <Globe size={11} className="text-gray-400" />
                          {guest.country_name}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                      {guest.total_bookings}
                    </td>

                    <td className="px-4 py-3 text-xs text-gray-600">
                      {formatGuestPreviewDate(guest.last_visit_date)}
                    </td>

                    <td className="px-4 py-3">
                      {guest.booking_methods ? (
                        <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          {guest.booking_methods}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">Direct</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {chip ? (
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${chip.className}`}
                        >
                          {chip.label}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-right">
                      {hasBalance ? (
                        <span className="font-semibold text-red-500">
                          {guest.outstanding_credit_balance.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && !error && guests.length > 0 && (
        <PaginationControls
          pagination={{
            page: activePage,
            pageSize,
            totalItems,
            totalPages,
            hasNextPage: activePage < totalPages,
            hasPreviousPage: activePage > 1,
          }}
          itemLabel={totalItems !== 1 ? 'guests' : 'guest'}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};
